import { normalizeDocumentCurrency } from "@/modules/quotation-invoice/domain/document-currencies";
import type {
    QuotationInvoiceCustomerListItem,
    QuotationInvoiceDocumentItemDTO,
    QuotationInvoiceDocumentListItem,
} from "@/modules/quotation-invoice/domain/types";
import {
    QUOTATION_INVOICE_CUSTOMER_FILTER_ALL,
    QUOTATION_INVOICE_CUSTOMER_UNCATEGORIZED,
} from "@/modules/quotation-invoice/domain/types";
import { useDisclosure } from "@/shared/hooks/useDisclosure";
import { config } from "@/shared/lib/config";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import {
    Button,
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    Checkbox,
    Input,
    Label,
    Textarea,
    toast,
} from "@devhop/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
    FileTextIcon,
    Settings2Icon,
    SparklesIcon,
    UsersIcon,
} from "lucide-react";
import { nanoid } from "nanoid";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { quotationInvoiceApi } from "../api/client";
import {
    quotationInvoiceKeys,
    useQuotationInvoiceCustomerCategoriesQuery,
    useQuotationInvoiceDocumentTypesQuery,
    useQiHeadOfficeBranchesQuery,
} from "../api/queries";
import { formatDocumentCurrency } from "../lib/format-document-currency";
import { CustomerManagementModal } from "./CustomerManagementModal";
import { DocumentLineQtyByUnitSummary } from "./DocumentLineQtyByUnitSummary";
import { documentCreateFormSamples } from "./documentCreateFormSamples";
import {
  findPaymentTermsPresetId,
  getPaymentTermsPresetText,
  paymentTermsPresetSelectOptions,
} from "./paymentTermsPresets";
import {
    QiDocumentLineEditor,
    createEmptyQiLineItem,
    type QiLineItemRow,
} from "./QiDocumentLineEditor";
import type { DocumentCreateStockPrefill } from "../lib/qi-create-prefill";

const DEFAULT_TAX_RATE = 0.1;

/** LAK / THB / USD ເທົ່ານັ້ນ */
const DOCUMENT_CURRENCY_OPTIONS = [
  { value: "LAK", label: "LAK — ກີບ (KIP)" },
  { value: "THB", label: "THB — ບາດ (Baht)" },
  { value: "USD", label: "USD — US Dollar" },
] as const;
/** Document type code from DB (e.g. quotation, invoice, receipt or custom). */
const DOCUMENT_NUMBER_PREFIX: Record<string, string> = {
  quotation: "QUO",
  invoice: "INV",
  receipt: "RCP",
};

type LineItemRow = QiLineItemRow;

function mapItemToLineRow(item: QuotationInvoiceDocumentItemDTO): LineItemRow {
  const lineKind = item.productId
    ? "product"
    : item.rawMaterialId
      ? "raw_material"
      : item.serviceId
        ? "service"
        : "free_text";
  const catalogLabel =
    item.serviceCode && item.serviceName
      ? `${item.serviceCode} — ${item.serviceName}`
      : item.productCode && item.productName
        ? `${item.productCode} — ${item.productName}`
        : "";
  return {
    id: nanoid(),
    lineKind,
    productId: item.productId ?? "",
    rawMaterialId: item.rawMaterialId ?? "",
    serviceId: item.serviceId ?? "",
    catalogLabel,
    description: item.description,
    detail: item.detail ?? "",
    quantity: item.quantity,
    unit: item.unit ?? "",
    unitPrice: item.unitPrice,
  };
}

function toYyyyMmDd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function suggestDocumentNumber(type: string): string {
  const prefix = DOCUMENT_NUMBER_PREFIX[type] ?? "DOC";
  const year = new Date().getFullYear();
  return `${prefix}-${year}-001`;
}

export type DocumentCreateFormInitialData = {
  document: QuotationInvoiceDocumentListItem;
  items: QuotationInvoiceDocumentItemDTO[];
};

export function DocumentCreateForm({
  onSubmit,
  submitting,
  initialData,
  stockPrefill = null,
  onOpenDocumentTypeModal,
}: {
  onSubmit: (payload: {
    documentType: string;
    status: "draft" | "issued";
    documentNumber: string;
    customerId: string | null;
    customerName: string;
    customerAddress: string | null;
    customerContact: string | null;
    issueDate: string | null;
    dueDate: string | null;
    referencePo: string | null;
    paymentTerms: string | null;
    notes: string | null;
    templateId: string | null;
    items: Array<{
      description?: string;
      detail?: string | null;
      quantity: number;
      unit: string | null;
      unitPrice: number;
      discountAmount?: number;
      taxRate?: number;
      productId?: string | null;
      rawMaterialId?: string | null;
    }>;
    discountAmount: number;
    taxRate: number;
    depositPercent: number;
    currency: string;
    showSignature: boolean;
    stockBranchId?: string | null;
  }) => void;
  submitting?: boolean;
  initialData?: DocumentCreateFormInitialData;
  stockPrefill?: DocumentCreateStockPrefill | null;
  /** When set, show "ຈັດການປະເພດເອກະສານ" button that opens document type management modal */
  onOpenDocumentTypeModal?: () => void;
}) {
  const doc = initialData?.document;
  const initialItems = initialData?.items ?? [];

  const [documentType, setDocumentType] = useState<string>(
    () => doc?.documentType ?? "quotation",
  );
  const { data: documentTypesData } = useQuotationInvoiceDocumentTypesQuery({
    limit: 50,
  });
  const documentTypeOptions = useMemo(() => {
    const list = documentTypesData?.items ?? [];
    return [...list]
      .sort((a, b) => a.orderIndex - b.orderIndex)
      .map((t) => ({ value: t.code, label: t.name }));
  }, [documentTypesData?.items]);
  const [docNumberManuallyEdited, setDocNumberManuallyEdited] = useState(false);
  useEffect(() => {
    if (documentTypeOptions.length === 0) return;
    const codes = documentTypeOptions.map((o) => o.value);
    if (!codes.includes(documentType)) {
      setDocumentType(codes[0] ?? "quotation");
      setDocNumberManuallyEdited(false);
    }
  }, [documentTypeOptions, documentType]);
  const [customerId, setCustomerId] = useState<string>(
    () => doc?.customerId ?? "",
  );
  const [customerSnapshot, setCustomerSnapshot] =
    useState<QuotationInvoiceCustomerListItem | null>(() =>
      doc
        ? {
            id: doc.customerId ?? "",
            name: doc.customerName,
            address: doc.customerAddress,
            contact: doc.customerContact,
            taxId: null,
            categoryId: null,
            categoryName: null,
            isActive: true,
          }
        : null,
    );
  const customerModal = useDisclosure();
  const queryClient = useQueryClient();
  const [documentNumber, setDocumentNumber] = useState(() => {
    const num = doc?.documentNumber?.trim();
    return num ?? "";
  });
  const [issueDate, setIssueDate] = useState(() =>
    doc?.issueDate
      ? toYyyyMmDd(new Date(doc.issueDate))
      : toYyyyMmDd(new Date()),
  );
  const [dueDate, setDueDate] = useState(() => {
    if (doc?.dueDate) return toYyyyMmDd(new Date(doc.dueDate));
    const d = new Date();
    d.setMonth(d.getMonth() + 1);
    return toYyyyMmDd(d);
  });
  const issueYear = useMemo(() => {
    if (!issueDate?.trim()) return new Date().getFullYear();
    const y = Number.parseInt(issueDate.slice(0, 4), 10);
    return Number.isFinite(y) ? y : new Date().getFullYear();
  }, [issueDate]);
  const nextNumberQuery = useQuery({
    queryKey: [
      "quotation-invoice",
      "next-number",
      documentType,
      issueYear,
    ] as const,
    queryFn: () =>
      quotationInvoiceApi.getNextDocumentNumber({
        documentType,
        year: issueYear,
      }),
    enabled: !doc && Boolean(documentType),
  });

  useEffect(() => {
    if (doc || docNumberManuallyEdited) return;
    const n = nextNumberQuery.data?.documentNumber;
    if (n) setDocumentNumber(n);
  }, [doc, docNumberManuallyEdited, nextNumberQuery.data?.documentNumber]);

  const prevIssueYearRef = useRef<number | null>(null);
  useEffect(() => {
    if (doc) return;
    if (prevIssueYearRef.current === null) {
      prevIssueYearRef.current = issueYear;
      return;
    }
    if (prevIssueYearRef.current !== issueYear) {
      setDocNumberManuallyEdited(false);
      prevIssueYearRef.current = issueYear;
    }
  }, [doc, issueYear]);
  const [referencePo, setReferencePo] = useState("");
  const [items, setItems] = useState<LineItemRow[]>(() =>
    initialItems.length > 0
      ? initialItems.map(mapItemToLineRow)
      : [createEmptyQiLineItem(nanoid())],
  );
  const [stockBranchId, setStockBranchId] = useState(
    () => doc?.stockBranchId ?? "",
  );
  const hqBranches = useQiHeadOfficeBranchesQuery();
  const [notes, setNotes] = useState(() => doc?.notes ?? "");
  const [paymentTerms, setPaymentTerms] = useState(
    () => doc?.paymentTerms ?? "",
  );
  const paymentTermsPresetId = useMemo(
    () => findPaymentTermsPresetId(paymentTerms),
    [paymentTerms],
  );
  const [vatEnabled, setVatEnabled] = useState(() => {
    if (!doc) return true;
    return Number(doc.taxAmount) > 0;
  });
  const [taxRate, setTaxRate] = useState(() => {
    if (!doc) return DEFAULT_TAX_RATE;
    const base = doc.subtotal - doc.discountAmount;
    if (base > 0) {
      const r = doc.taxAmount / base;
      return Number.isFinite(r) ? r : DEFAULT_TAX_RATE;
    }
    return DEFAULT_TAX_RATE;
  });
  const [discountAmount, setDiscountAmount] = useState(
    () => doc?.discountAmount ?? 0,
  );
  const [depositPercent, setDepositPercent] = useState(
    () => Number(doc?.depositPercent ?? 0),
  );
  const [templateId, setTemplateId] = useState<string>(
    () => doc?.templateId ?? "",
  );
  const [currency, setCurrency] = useState(() =>
    normalizeDocumentCurrency(doc?.currency),
  );
  const [showSignature, setShowSignature] = useState(
    () => doc?.showSignature ?? true,
  );

  const formatMoney = useCallback(
    (value: number) => formatDocumentCurrency(value, currency),
    [currency],
  );

  const issueDateLabel =
    documentType === "receipt" ? "ວັນທີຮັບເງິນ / ອອກໃບຮັບ" : "ວັນທີອອກ";
  const dueDateLabel =
    documentType === "quotation"
      ? "ວັນໝົດອາຍຸຂໍ້ສະເໜີ (ຖ້າມີ)"
      : documentType === "invoice"
        ? "ວັນຄົບກໍານົດຊຳລະ (ງວດນີ້)"
        : "ວັນຄົບກໍານົດຊຳລະ";

  const [sampleIndex, setSampleIndex] = useState(0);
  const isDev =
    import.meta.hot !== undefined || import.meta.env?.MODE === "development";

  const { data: customerCategoriesData } =
    useQuotationInvoiceCustomerCategoriesQuery({});
  const customerCategoryFilterOptions = useMemo(() => {
    const items = customerCategoriesData?.items ?? [];
    const activeSorted = [...items]
      .filter((c) => c.isActive)
      .sort(
        (a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name),
      );
    return [
      { value: QUOTATION_INVOICE_CUSTOMER_FILTER_ALL, label: "ທັງໝົດ" },
      {
        value: QUOTATION_INVOICE_CUSTOMER_UNCATEGORIZED,
        label: "ບໍ່ມີຫມວດຫມູ່",
      },
      ...activeSorted.map((c) => ({ value: c.id, label: c.name })),
    ];
  }, [customerCategoriesData?.items]);

  const [customerListCategoryFilter, setCustomerListCategoryFilter] =
    useState(QUOTATION_INVOICE_CUSTOMER_FILTER_ALL);
  const [customerSearch, setCustomerSearch] = useState("");

  const customerQueryFn = useCallback(
    async (params: { search: string; pageParam: number }) => {
      const limit = 20;
      const skip = (params.pageParam - 1) * limit;
      const result = await quotationInvoiceApi.getCustomers({
        q: params.search || undefined,
        limit,
        skip,
        ...(customerListCategoryFilter !== QUOTATION_INVOICE_CUSTOMER_FILTER_ALL
          ? { categoryId: customerListCategoryFilter }
          : {}),
      });
      const nextPage =
        skip + result.items.length < result.total ? params.pageParam + 1 : null;
      return { items: result.items, nextPage };
    },
    [customerListCategoryFilter],
  );

  const customerPreloadFn = useCallback(async (id: string) => {
    return quotationInvoiceApi.getCustomer(id);
  }, []);

  const hqBranchOptions = useMemo(
    () =>
      (hqBranches.data?.items ?? []).map((branch) => ({
        value: branch.id,
        label: `${branch.code} — ${branch.name}`,
      })),
    [hqBranches.data?.items],
  );

  useEffect(() => {
    if (doc?.stockBranchId) return;
    if (stockBranchId) return;
    const first = hqBranches.data?.items?.[0];
    if (first) setStockBranchId(first.id);
  }, [doc?.stockBranchId, stockBranchId, hqBranches.data?.items]);

  const stockPrefillAppliedRef = useRef(false);
  useEffect(() => {
    if (doc || !stockPrefill || stockPrefillAppliedRef.current) return;
    stockPrefillAppliedRef.current = true;
    if (stockPrefill.documentType) setDocumentType(stockPrefill.documentType);
    if (stockPrefill.stockBranchId) setStockBranchId(stockPrefill.stockBranchId);
    if (stockPrefill.notes) setNotes(stockPrefill.notes);
    const line = stockPrefill.line;
    if (!line?.productId && !line?.rawMaterialId) return;
    const row: LineItemRow = {
      id: nanoid(),
      lineKind: line.productId ? "product" : "raw_material",
      productId: line.productId ?? "",
      rawMaterialId: line.rawMaterialId ?? "",
      catalogLabel:
        line.productCode && line.productName
          ? `${line.productCode} — ${line.productName}`
          : "",
      description:
        line.description ??
        (line.productCode && line.productName
          ? `${line.productCode} — ${line.productName}`
          : line.productName ?? line.productCode ?? ""),
      detail: "",
      quantity: line.quantity,
      unit: line.unit ?? "",
      unitPrice: line.unitPrice ?? 0,
    };
    setItems([row]);
  }, [doc, stockPrefill]);

  const subtotal = useMemo(() => {
    return items.reduce((sum, row) => {
      const lineTotal = row.quantity * row.unitPrice;
      return sum + lineTotal;
    }, 0);
  }, [items]);

  const effectiveTaxRate = vatEnabled ? taxRate : 0;
  const taxAmount = useMemo(() => {
    return (
      Math.round((subtotal - discountAmount) * effectiveTaxRate * 100) / 100
    );
  }, [subtotal, discountAmount, effectiveTaxRate]);

  const total = useMemo(() => {
    return Math.round((subtotal - discountAmount + taxAmount) * 100) / 100;
  }, [subtotal, discountAmount, taxAmount]);

  const depositAmount = useMemo(() => {
    return Math.round(total * depositPercent * 100) / 100;
  }, [total, depositPercent]);

  const handleCustomerChange = (id: string) => {
    setCustomerId(id);
    setCustomerSearch("");
    if (!id) {
      setCustomerSnapshot(null);
      return;
    }
    quotationInvoiceApi
      .getCustomer(id)
      .then((c) => setCustomerSnapshot(c ?? null));
  };

  const handleCustomerModalRefresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: quotationInvoiceKeys.all });
  }, [queryClient]);

  const handleSelectCustomerFromModal = useCallback((id: string) => {
    setCustomerId(id);
    setCustomerSearch("");
    quotationInvoiceApi
      .getCustomer(id)
      .then((c) => setCustomerSnapshot(c ?? null));
  }, []);

  const handleDocumentTypeSelect = useCallback(
    (code: string) => {
      setDocumentType(code);
      setDocNumberManuallyEdited(false);
      if (doc?.id) {
        void quotationInvoiceApi
          .getNextDocumentNumber({
            documentType: code,
            year: issueYear,
            excludeDocumentId: doc.id,
          })
          .then((r) => setDocumentNumber(r.documentNumber))
          .catch(() => setDocumentNumber(suggestDocumentNumber(code)));
      }
    },
    [doc?.id, issueYear],
  );

  const handleAutoFill = () => {
    const sample = documentCreateFormSamples[sampleIndex];
    if (!sample) return;
    setDocumentType(sample.documentType);
    setDocumentNumber(sample.documentNumber);
    setDocNumberManuallyEdited(true);
    setIssueDate(sample.issueDate);
    setDueDate(sample.dueDate);
    setReferencePo(sample.referencePo);
    setItems(
      sample.items.map((row) => ({
        ...createEmptyQiLineItem(nanoid()),
        lineKind: "free_text" as const,
        description: row.description,
        detail: row.detail ?? "",
        quantity: row.quantity,
        unit: row.unit,
        unitPrice: row.unitPrice,
      })),
    );
    setNotes(sample.notes);
    setPaymentTerms(sample.paymentTerms);
    setTaxRate(sample.taxRate);
    setVatEnabled(sample.taxRate > 0);
    setDiscountAmount(sample.discountAmount);
    setCurrency(normalizeDocumentCurrency(sample.currency));
    setShowSignature(true);
    setSampleIndex((prev) => (prev + 1) % documentCreateFormSamples.length);
    toast.success(
      `ຕື່ມຂໍ້ມູນຕົວຢ່າງສຳເລັດ (${sampleIndex + 1}/${documentCreateFormSamples.length}). ກະລຸນາເລືອກລູກຄ້າ`,
    );
  };

  const addLineItem = () => {
    setItems((prev) => [...prev, createEmptyQiLineItem(nanoid())]);
  };

  const isLineValid = (row: LineItemRow) => {
    if (row.quantity <= 0) return false;
    if (row.lineKind === "free_text") return Boolean(row.description.trim());
    if (row.lineKind === "product") return Boolean(row.productId);
    if (row.lineKind === "service") return Boolean(row.serviceId);
    return Boolean(row.rawMaterialId);
  };

  const handleSubmit = (asDraft: boolean) => {
    const customerName = customerSnapshot?.name?.trim();
    if (!customerName) return;
    const validItems = items.filter(isLineValid);
    if (validItems.length === 0) return;

    if (!asDraft) {
      if (
        (documentType === "invoice" || documentType === "receipt") &&
        !issueDate.trim()
      ) {
        toast.error(
          documentType === "receipt"
            ? "ປ້ອນວັນທີຮັບເງິນ / ອອກໃບຮັບ"
            : "ປ້ອນວັນທີອອກເອກະສານ",
        );
        return;
      }
      if (documentType === "invoice" && !dueDate.trim()) {
        toast.error("ປ້ອນວັນຄົບກໍານົດຊຳລະສຳລັບໃບບິນ");
        return;
      }
    }

    onSubmit({
      documentType,
      status: asDraft ? "draft" : "issued",
      documentNumber:
        documentNumber.trim() || suggestDocumentNumber(documentType),
      customerId: customerId || null,
      customerName,
      customerAddress: customerSnapshot?.address ?? null,
      customerContact: customerSnapshot?.contact ?? null,
      issueDate: asDraft ? null : issueDate,
      dueDate: documentType === "receipt" ? null : dueDate || null,
      referencePo: referencePo.trim() || null,
      paymentTerms: paymentTerms.trim() || null,
      notes:
        [referencePo.trim(), notes.trim()].filter(Boolean).join("\n") || null,
      templateId: templateId.trim() || null,
      items: validItems.map((row) => ({
        description:
          row.lineKind === "free_text"
            ? row.description.trim()
            : row.description.trim() || undefined,
        detail: row.detail.trim() ? row.detail.trim() : null,
        quantity: row.quantity,
        unit: row.unit.trim() || null,
        unitPrice: row.unitPrice,
        productId:
          row.lineKind === "product" ? row.productId || null : null,
        rawMaterialId:
          row.lineKind === "raw_material" ? row.rawMaterialId || null : null,
        serviceId:
          row.lineKind === "service" ? row.serviceId || null : null,
      })),
      discountAmount,
      taxRate: vatEnabled ? taxRate : 0,
      depositPercent,
      currency: normalizeDocumentCurrency(currency),
      showSignature,
      stockBranchId: stockBranchId.trim() || null,
    });
  };

  const canSubmit =
    !!customerSnapshot?.name?.trim() && items.some(isLineValid);

  return (
    <div className="space-y-6">
      {/* Section 1: Document & Customer */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-semibold text-base">
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs">
              1
            </span>
            ຂໍ້ມູນເອກະສານ & ລູກຄ້າ
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <div className="mb-2 flex items-center justify-between gap-2">
              <Label>ປະເພດເອກະສານ</Label>
              {onOpenDocumentTypeModal && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={onOpenDocumentTypeModal}
                  className="gap-1"
                >
                  <Settings2Icon className="h-4 w-4" />
                  ຈັດການປະເພດເອກະສານ
                </Button>
              )}
            </div>
            <SimpleSelect
              value={documentType}
              onValueChange={handleDocumentTypeSelect}
              placeholder="ເລືອກປະເພດເອກະສານ"
              className="h-9 min-w-[180px]"
              options={documentTypeOptions}
            />
          </div>
          <div>
            <Label className="flex items-center gap-1.5">
              <FileTextIcon className="size-4" />
              ແມ່ແບບ (ສຳລັບພິມ/PDF)
            </Label>
            <InfiniteCombobox<{ id: string; name: string }>
              queryKey={["template", "lookup", "quotation-invoice"]}
              queryFn={async ({ search, pageParam }) => {
                const limit = 20;
                const skip = (pageParam - 1) * limit;
                const params = new URLSearchParams({
                  limit: String(limit),
                  skip: String(skip),
                });
                if (search?.trim()) params.set("q", search.trim());
                const res = await fetch(
                  `${config.apiUrl}/template/lookup?${params}`,
                  { credentials: "include" },
                );
                if (!res.ok) return { items: [], nextPage: null };
                const data = await res.json();
                const nextPage =
                  skip + (data.items?.length ?? 0) < (data.total ?? 0)
                    ? pageParam + 1
                    : null;
                return { items: data.items ?? [], nextPage };
              }}
              preloadQueryFn={async (id) => {
                const res = await fetch(
                  `${config.apiUrl}/template/lookup/${id}`,
                  { credentials: "include" },
                );
                if (!res.ok) return null;
                const data = await res.json();
                const item = data?.item;
                return item ? { id: item.id, name: item.name } : null;
              }}
              getLabel={(item) => item.name}
              getValue={(item) => item.id}
              value={templateId}
              onValueChange={setTemplateId}
              placeholder="ເລືອກແມ່ແບບ (ບໍ່ບັງຄັບ)"
              clearable
              className="mt-1"
            />
          </div>
          <div>
            <div className="mb-2 flex items-center justify-between gap-2">
              <Label>ເລືອກລູກຄ້າ</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={customerModal.open}
                className="gap-1"
              >
                <UsersIcon className="h-4 w-4" />
                ຈັດການລູກຄ້າ
              </Button>
            </div>
            <div className="mb-2 space-y-2">
              <Label className="text-muted-foreground text-xs">
                ກອງລາຍການຕາມຫມວດຫມູ່
              </Label>
              <SimpleSelect
                value={customerListCategoryFilter}
                onValueChange={setCustomerListCategoryFilter}
                options={customerCategoryFilterOptions}
                className="h-9 w-full max-w-md"
              />
            </div>
            <Input
              id="customerSearch"
              value={customerSearch}
              onChange={(e) => setCustomerSearch(e.target.value)}
              placeholder="ຄົ້ນຫາຊື່, ເບີໂທ ຫຼື ທີ່ຢູ່ລູກຄ້າ..."
              className="mb-2"
            />
            <InfiniteCombobox<QuotationInvoiceCustomerListItem>
              queryKey={[
                "quotation-invoice",
                "customers",
                customerListCategoryFilter,
              ]}
              queryFn={customerQueryFn}
              preloadQueryFn={customerPreloadFn}
              getLabel={(item) =>
                item.categoryName
                  ? `${item.name} (${item.categoryName})`
                  : item.name
              }
              getValue={(item) => item.id}
              value={customerId}
              onValueChange={handleCustomerChange}
              placeholder="ເລືອກລູກຄ້າຈາກລາຍການ"
              searchValue={customerSearch}
              onSearchChange={setCustomerSearch}
              clearable
            />
            <p className="mt-1 text-muted-foreground text-sm">
              ລາຍລະອຽດລູກຄ້າຈະສະແດງເມື່ອເລືອກແລ້ວ
            </p>
          </div>
          <CustomerManagementModal
            open={customerModal.isOpen}
            onOpenChange={(open) =>
              open ? customerModal.open() : customerModal.close()
            }
            onRefresh={handleCustomerModalRefresh}
            onSelectCustomer={handleSelectCustomerFromModal}
          />
          {customerSnapshot && (
            <div className="rounded-lg border border-dashed p-4">
              <p className="font-medium">{customerSnapshot.name}</p>
              {customerSnapshot.categoryName && (
                <p className="text-muted-foreground text-sm">
                  ຫມວດຫມູ່: {customerSnapshot.categoryName}
                </p>
              )}
              {customerSnapshot.address && (
                <p className="text-muted-foreground text-sm">
                  {customerSnapshot.address}
                </p>
              )}
              {customerSnapshot.contact && (
                <p className="text-muted-foreground text-sm">
                  {customerSnapshot.contact}
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Section 2: Document Details */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-semibold text-base">
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs">
              2
            </span>
            ລາຍລະອຽດເອກະສານ
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="docNumber">ເລກທີເອກະສານ</Label>
              {!doc && (
                <p className="mt-0.5 text-muted-foreground text-xs">
                  ສ້າງອັດຕະໂນມັດຈາກເລກລ່າສຸດຕາມປະເພດ ແລະ ປີວັນທີອອກ (ແກ້ໄຂເອງໄດ້)
                </p>
              )}
              <Input
                id="docNumber"
                value={documentNumber}
                onChange={(e) => {
                  setDocNumberManuallyEdited(true);
                  setDocumentNumber(e.target.value);
                }}
                placeholder={
                  !doc && nextNumberQuery.isPending
                    ? "ກຳລັງສ້າງເລກທີ..."
                    : "QUO-2026-001"
                }
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="issueDate">{issueDateLabel}</Label>
              <Input
                id="issueDate"
                type="date"
                value={issueDate}
                onChange={(e) => setIssueDate(e.target.value)}
                className="mt-1"
              />
            </div>
            {documentType !== "receipt" && (
              <div>
                <Label htmlFor="dueDate">{dueDateLabel}</Label>
                <Input
                  id="dueDate"
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  className="mt-1"
                />
              </div>
            )}
            <div className="sm:col-span-2">
              <Label htmlFor="referencePo">ເອກະສານອ້າງອີງ / ເລກ PO</Label>
              <Input
                id="referencePo"
                value={referencePo}
                onChange={(e) => setReferencePo(e.target.value)}
                placeholder="ຕົວຢ່າງ PO-45001"
                className="mt-1"
              />
            </div>
            <div className="sm:col-span-2">
              <Label>ສາຂາ HQ (ຈອງ/ຕັດສຕັອກ)</Label>
              <SimpleSelect
                value={stockBranchId}
                onValueChange={setStockBranchId}
                placeholder={
                  hqBranches.isLoading
                    ? "ກຳລັງໂຫຼດ..."
                    : "ເລືອກສຳນັກງານໃຫຍ່"
                }
                className="mt-1 h-9"
                options={hqBranchOptions}
              />
              <p className="mt-1 text-muted-foreground text-xs">
                ໃຊ້ສຳລັບ catalog stock scope — Phase 11
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Section 3: Line Items */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-semibold text-base">
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs">
              3
            </span>
            ລາຍການ
          </CardTitle>
          {documentType === "invoice" && (
            <p className="font-normal text-muted-foreground text-sm leading-relaxed">
              ຍອດ ແລະ ລາຍການຂອງງວດນີ້ — ປັບໃຫ້ຄົບກັບງວດທີ່ຕົກລົງ
              (ບໍ່ຈຳເປັນຕ້ອງເທົ່າຍອດໃບສະເໜີລາຄາທັງໝົດ)
            </p>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          <QiDocumentLineEditor
            items={items}
            onChange={setItems}
            formatMoney={formatMoney}
            stockBranchId={stockBranchId || undefined}
          />
          <Button
            type="button"
            variant="link"
            size="sm"
            className="gap-1 px-0 text-primary"
            onClick={addLineItem}
          >
            + ເພີ່ມລາຍການ
          </Button>
          <div className="flex justify-end">
            <div className="w-full max-w-xs space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">ຍອດລວມ:</span>
                <span>{formatMoney(subtotal)}</span>
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-muted-foreground">ສ່ວນລົດ (ເອກະສານ):</span>
                <Input
                  type="number"
                  min={0}
                  step={0.01}
                  value={discountAmount || ""}
                  onChange={(e) =>
                    setDiscountAmount(Number(e.target.value) || 0)
                  }
                  className="h-8 w-24 shrink-0 text-right"
                />
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="doc-vat-enabled"
                  checked={vatEnabled}
                  onCheckedChange={(v) => setVatEnabled(Boolean(v))}
                />
                <label
                  htmlFor="doc-vat-enabled"
                  className="cursor-pointer text-muted-foreground text-sm"
                >
                  VAT
                </label>
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-muted-foreground">VAT (%):</span>
                <Input
                  type="number"
                  min={0}
                  max={100}
                  step={0.01}
                  value={Math.round(taxRate * 10000) / 100}
                  onChange={(e) =>
                    setTaxRate(
                      Math.min(
                        1,
                        Math.max(0, (Number(e.target.value) || 0) / 100),
                      ),
                    )
                  }
                  disabled={!vatEnabled}
                  className="h-8 w-24 shrink-0 text-right"
                  aria-label="ອັດຕາ VAT ເປີເຊັນ"
                />
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">ຍອດ VAT:</span>
                <span>{formatMoney(taxAmount)}</span>
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-muted-foreground">ມັດຈຳ (%):</span>
                <Input
                  type="number"
                  min={0}
                  max={100}
                  step={0.01}
                  value={Math.round(depositPercent * 10000) / 100}
                  onChange={(e) =>
                    setDepositPercent(
                      Math.min(
                        1,
                        Math.max(0, (Number(e.target.value) || 0) / 100),
                      ),
                    )
                  }
                  className="h-8 w-24 shrink-0 text-right"
                  aria-label="ອັດຕາມັດຈຳ ເປີເຊັນ"
                />
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">ຍອດມັດຈຳ:</span>
                <span>{formatMoney(depositAmount)}</span>
              </div>
              <div className="flex justify-between border-t pt-2 font-bold text-primary">
                <span>ລວມທັງໝົດ:</span>
                <span>{formatMoney(total)}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card
        className="border-primary/25 print:hidden"
        data-tourid="document-create-qty-by-unit"
      >
        <CardHeader className="pb-3">
          <CardTitle className="font-semibold text-base">
            ສະຫຼຸບຈຳນວນຕາມຫນ່ວຍ
          </CardTitle>
          <p className="font-normal text-muted-foreground text-sm leading-relaxed">
            ອັບເດດຕາມລາຍການຂ້າງເທິງ — ສຳລັບເບິ່ງໃນຟອມເທົ່ານັ້ນ (ບໍ່ພິມໃນເອກະສານ)
          </p>
        </CardHeader>
        <CardContent>
          <DocumentLineQtyByUnitSummary
            items={items}
            alwaysShow
            hideHeader
          />
        </CardContent>
      </Card>

      {/* Section 4: Terms & Notes */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-semibold text-base">
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs">
              4
            </span>
            ເງື່ອນໄຂ & ຫມາຍເຫດ
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="currency">ສະກຸນເງິນ</Label>
              <SimpleSelect
                id="currency"
                value={currency}
                onValueChange={(v) => setCurrency(normalizeDocumentCurrency(v))}
                placeholder="ເລືອກສະກຸນເງິນ"
                className="mt-1 h-9"
                options={[...DOCUMENT_CURRENCY_OPTIONS]}
              />
            </div>
            <div className="flex items-end pb-1 sm:col-span-2">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="doc-show-signature"
                  checked={showSignature}
                  onCheckedChange={(v) => setShowSignature(Boolean(v))}
                />
                <label
                  htmlFor="doc-show-signature"
                  className="cursor-pointer text-muted-foreground text-sm"
                >
                  ມີລາຍເຊັນ & ຕຣາປະທັບໃນເອກະສານ (ຈາກການຕັ້ງຄ່າຜູ້ອອກເອກະສານ)
                </label>
              </div>
            </div>
          </div>
          <div>
            <Label htmlFor="notes">ຫມາຍເຫດພາຍໃນ</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="ເພີ່ມຫມາຍເຫດສຳລັບບັນທຶກພາຍໃນ..."
              className="mt-1 min-h-[80px]"
              rows={3}
            />
          </div>
          <div>
            <Label htmlFor="paymentTerms">
              {documentType === "quotation"
                ? "ເງື່ອນໄຂການຊຳລະ (ລູກຄ້າ) — ລະບຸໂຄງງວດຖ້າມີ"
                : "ເງື່ອນໄຂການຊຳລະ (ລູກຄ້າ)"}
            </Label>
            <SimpleSelect
              id="paymentTermsPreset"
              value={paymentTermsPresetId}
              onValueChange={(id) => {
                const text = getPaymentTermsPresetText(id);
                if (text !== undefined) setPaymentTerms(text);
              }}
              placeholder="— ເລືອກແມ່ແບບ —"
              className="mt-1 h-9"
              options={paymentTermsPresetSelectOptions}
            />
            <Textarea
              id="paymentTerms"
              value={paymentTerms}
              onChange={(e) => setPaymentTerms(e.target.value)}
              className="mt-2 min-h-[80px]"
              rows={4}
              placeholder="ລະບຸເງື່ອນໄຂການຊຳລະ (ປ່ອຍວ່າງໄດ້)..."
            />
            {documentType === "quotation" && (
              <p className="mt-1 text-muted-foreground text-xs">
                ເລືອກແມ່ແບບແລ້ວແກ້ໄຂເພີ່ມເຕີມໄດ້ — ຫຼື ພິມເອງ
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Spacer for floating bar */}
      <div className="h-20" />

      {/* Floating action bar */}
      <div className="sticky bottom-2 z-10 flex flex-col gap-3 border-t bg-background/95 pt-4 backdrop-blur-sm sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <div className="flex min-w-0 flex-1 flex-col gap-2 sm:max-w-[55%]">
          <DocumentLineQtyByUnitSummary
            items={items}
            alwaysShow
            variant="compact"
          />
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-muted-foreground text-sm">
              ກະລຸນາໃສ່ຂໍ້ມູນສຳຄັນໃຫ້ຄົບກ່ອນບັນທຶກ
            </p>
          {isDev && !initialData && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleAutoFill}
              disabled={submitting}
              className="gap-1"
            >
              <SparklesIcon className="h-4 w-4" />
              ຕົວຢ່າງ ({sampleIndex + 1}/{documentCreateFormSamples.length})
            </Button>
          )}
          </div>
        </div>
        <div className="flex shrink-0 gap-2">
          <Button
            type="button"
            variant="outline"
            disabled={!canSubmit || submitting}
            onClick={() => handleSubmit(true)}
          >
            ບັນທຶກເປັນຮ່າງ
          </Button>
          <Button
            type="button"
            disabled={!canSubmit || submitting}
            onClick={() => handleSubmit(false)}
          >
            ອອກເອກະສານ
          </Button>
        </div>
      </div>
    </div>
  );
}
